import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const COMPONENT_SPECS = {
  VFD: [
    "input_voltage",
    "output_voltage",
    "power_rating_kw",
    "input_frequency",
    "output_frequency",
    "dc_bus_voltage",
    "input_current_a",
    "output_current_a",
    "switching_frequency_khz",
    "efficiency_percent",
    "power_factor",
    "harmonic_filter",
    "harmonic_distortion_thd",
    "cooling_method",
    "enclosure_rating",
    "insulation_class",
    "overload_capacity",
    "braking_method",
    "protection_functions",
    "communication_protocol",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  TRANS: [
    "primary_voltage",
    "secondary_voltage",
    "power_rating_kva",
    "frequency",
    "vector_group",
    "impedance_voltage_percent",
    "no_load_losses_w",
    "full_load_losses_w",
    "no_load_current_percent",
    "insulation_class",
    "temperature_rise_k",
    "cooling_type",
    "enclosure_rating",
    "tapping_range",
    "turns_ratio",
    "primary_current_a",
    "secondary_current_a",
    "fault_current_contribution_ka",
    "short_circuit_withstand_ka",
    "short_circuit_duration_s",
    "winding_configuration",
    "partial_discharge_pc",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  MOTOR: [
    "rated_power_kw",
    "rated_voltage",
    "frequency",
    "rated_speed_rpm",
    "synchronous_speed_rpm",
    "number_of_poles",
    "rated_current_a",
    "starting_current_a",
    "power_factor",
    "efficiency_percent",
    "efficiency_class",
    "insulation_class",
    "temperature_rise_k",
    "enclosure_rating",
    "mounting_type",
    "frame_size",
    "bearing_type",
    "cooling_method",
    "starting_method",
    "service_factor",
    "duty_cycle",
    "locked_rotor_torque_percent",
    "breakdown_torque_percent",
    "moment_of_inertia_kgm2",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  GEN: [
    "rated_power_kva",
    "rated_power_kw",
    "rated_voltage",
    "frequency",
    "power_factor",
    "speed_rpm",
    "number_of_poles",
    "rated_current_a",
    "efficiency_percent",
    "insulation_class",
    "temperature_rise_k",
    "enclosure_rating",
    "excitation_type",
    "avr_type",
    "voltage_regulation_percent",
    "transient_voltage_dip_percent",
    "frequency_regulation_percent",
    "subtransient_reactance_xd",
    "transient_reactance_xd",
    "synchronous_reactance_xd",
    "short_circuit_ratio",
    "fault_current_contribution_ka",
    "parallel_operation",
    "cooling_method",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  SWBDS: [
    "bus_voltage",
    "bus_current_rating_a",
    "frequency",
    "fault_level_ka",
    "fault_duration_s",
    "number_of_incoming_feeders",
    "incoming_breaker_rating_a",
    "number_of_outgoing_feeders",
    "busbar_material",
    "busbar_rating_a",
    "busbar_configuration",
    "enclosure_rating",
    "protection_standard",
    "form_of_separation",
    "breaking_capacity_ka",
    "arc_flash_mitigation",
    "protection_relay_type",
    "metering",
    "neutral_earthing",
    "cooling_method",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  UPS: [
    "input_voltage",
    "output_voltage",
    "power_rating_kva",
    "power_rating_kw",
    "frequency",
    "input_frequency_range",
    "output_frequency_regulation",
    "output_voltage_regulation",
    "efficiency_percent",
    "topology",
    "battery_type",
    "battery_autonomy_min",
    "battery_voltage",
    "battery_recharge_time_h",
    "bypass_rating_a",
    "static_bypass",
    "maintenance_bypass",
    "overload_capacity",
    "harmonic_distortion_thd",
    "power_factor_input",
    "power_factor_output",
    "enclosure_rating",
    "insulation_class",
    "cooling_method",
    "mounting_arrangement",
    "communication_protocol",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  DBR: [
    "dc_bus_voltage",
    "peak_braking_power_kw",
    "continuous_braking_power_kw",
    "braking_duration_peak_s",
    "braking_duration_continuous",
    "duty_cycle",
    "resistance_value_ohm",
    "resistance_tolerance",
    "peak_current_a",
    "continuous_current_a",
    "energy_per_cycle_kwh",
    "insulation_class",
    "thermal_trip_temperature_c",
    "thermal_trip_reset",
    "temperature_monitoring",
    "number_of_resistor_banks",
    "resistor_element_material",
    "enclosure_rating",
    "cooling_method",
    "mounting_arrangement",
    "connected_vfd_tag",
    "connected_motor_tag",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
};

const DIMENSION_FIELDS = new Set([
  "max_height_mm",
  "max_width_mm",
  "max_depth_mm",
  "max_weight_kg",
  "floor_loading_kn_m2",
]);

const DIMENSION_LABELS = {
  max_height_mm: "Max Height — Do Not Exceed (mm)",
  max_width_mm: "Max Width — Do Not Exceed (mm)",
  max_depth_mm: "Max Depth — Do Not Exceed (mm)",
  max_weight_kg: "Max Weight — Do Not Exceed (kg)",
  floor_loading_kn_m2: "Max Floor Loading — Do Not Exceed (kN/m²)",
};

const COMPONENT_COLORS = {
  VFD: { bg: "#005E60", light: "#e6f4f4" },
  TRANS: { bg: "#007b7d", light: "#e6f5f5" },
  MOTOR: { bg: "#00843d", light: "#e6f5ec" },
  GEN: { bg: "#c47a00", light: "#fdf3e3" },
  SWBDS: { bg: "#5c35a0", light: "#f0eafa" },
  UPS: { bg: "#b00033", light: "#fce8ed" },
  DBR: { bg: "#0077b6", light: "#e6f2fb" },
};

const CLASSIFICATION_SOCIETIES = [
  "DNV",
  "LR",
  "ABS",
  "BV",
  "RINA",
  "CCS",
  "NK",
  "NAVSEA",
];

const VESSEL_TYPES = [
  "Offshore Supply Vessel",
  "Cargo Ship",
  "Tanker",
  "Ferry",
  "Cruise Ship",
  "Naval Vessel",
  "Research Vessel",
  "Dredger",
  "Tug",
  "Other",
];

const COMPAT_ICON = {
  pending: <HourglassEmptyIcon fontSize="small" sx={{ color: "#c47a00" }} />,
  pass: <CheckCircleIcon fontSize="small" sx={{ color: "#00843d" }} />,
  fail: <ErrorIcon fontSize="small" sx={{ color: "#b00033" }} />,
  warning: <WarningAmberIcon fontSize="small" sx={{ color: "#c47a00" }} />,
};

const STATUS_BORDER = {
  pass: "#00843d",
  fail: "#b00033",
  warning: "#c47a00",
  error: "#aaa",
};
const STATUS_BG = {
  pass: "#f0faf4",
  fail: "#fdf0f3",
  warning: "#fdf6e3",
  error: "#f8f9fa",
};
const STATUS_TEXT = {
  pass: "#00843d",
  fail: "#b00033",
  warning: "#c47a00",
  error: "#555",
};

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

const formatLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const formatDimLabel = (key) => DIMENSION_LABELS[key] || formatLabel(key);
const isDimField = (key) => DIMENSION_FIELDS.has(key);
const cleanIssueText = (text) =>
  text.replace(/_([a-z])/g, (_, c) => ` ${c}`).replace(/_/g, " ");

const fieldStyle = (isProblematic) => ({
  "& label.Mui-focused": { color: isProblematic ? "#b00033" : PRIMARY },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": {
      borderColor: isProblematic ? "#b00033" : undefined,
      borderWidth: isProblematic ? 2 : 1,
    },
    "&:hover fieldset": { borderColor: isProblematic ? "#b00033" : PRIMARY },
    "&.Mui-focused fieldset": {
      borderColor: isProblematic ? "#b00033" : PRIMARY,
    },
  },
  backgroundColor: isProblematic ? "#fff5f7" : "white",
  borderRadius: "8px",
});

function extractProblematicFields(issues = []) {
  const problematic = new Set();
  const allSpecKeys = Object.values(COMPONENT_SPECS).flat();

  issues.forEach((issue) => {
    const lower = issue.toLowerCase();
    allSpecKeys.forEach((key) => {
      if (lower.includes(key.replace(/_/g, " ")) || lower.includes(key))
        problematic.add(key);
    });
    if (lower.includes("voltage"))
      [
        "input_voltage",
        "output_voltage",
        "rated_voltage",
        "primary_voltage",
        "secondary_voltage",
        "bus_voltage",
      ].forEach((k) => problematic.add(k));
    if (lower.includes("frequency"))
      ["frequency", "input_frequency", "output_frequency"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("power"))
      ["power_rating_kw", "power_rating_kva", "rated_power_kw"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("enclosure") || lower.includes("ip"))
      problematic.add("enclosure_rating");
    if (lower.includes("insulation")) problematic.add("insulation_class");
    if (lower.includes("harmonic"))
      ["harmonic_filter", "harmonic_distortion_thd"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("fault"))
      ["fault_level_ka", "fault_current_contribution_ka"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("impedance"))
      problematic.add("impedance_voltage_percent");
    if (lower.includes("neutral") || lower.includes("earthing"))
      problematic.add("neutral_earthing");
    if (lower.includes("temperature") || lower.includes("rise"))
      problematic.add("temperature_rise_k");
    if (lower.includes("breaking")) problematic.add("breaking_capacity_ka");
    if (lower.includes("regulation"))
      ["voltage_regulation_percent", "output_voltage_regulation"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("autonomy") || lower.includes("battery"))
      problematic.add("battery_autonomy_min");
    if (lower.includes("topology")) problematic.add("topology");
    if (lower.includes("bypass"))
      ["static_bypass", "maintenance_bypass"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("braking") || lower.includes("peak"))
      ["peak_braking_power_kw", "continuous_braking_power_kw"].forEach((k) =>
        problematic.add(k),
      );
    if (lower.includes("resistance")) problematic.add("resistance_value_ohm");
    if (lower.includes("thermal") || lower.includes("trip"))
      ["thermal_trip_temperature_c", "temperature_monitoring"].forEach((k) =>
        problematic.add(k),
      );
  });
  return problematic;
}

// ══════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  // ESS
  const [essLoading, setEssLoading] = useState(null);
  const [essResult, setEssResult] = useState(null);
  const [essModalOpen, setEssModalOpen] = useState(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [essError, setEssError] = useState("");

  // Edit component
  const [editComp, setEditComp] = useState(null);
  const [editSpecs, setEditSpecs] = useState({});
  const [editTag, setEditTag] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Edit project
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editProjectData, setEditProjectData] = useState({});
  const [editProjectSaving, setEditProjectSaving] = useState(false);
  const [editProjectError, setEditProjectError] = useState("");

  // ── Fetch project ─────────────────────────────────────────
  const fetchProject = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
    } catch {
      setFetchError("Failed to load project. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // ── Delete component ──────────────────────────────────────
  const handleDeleteComponent = async (e, compId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this component? This cannot be undone."))
      return;
    setDeleting(compId);
    try {
      await api.delete(`/components/${compId}`);
      setProject((prev) => ({
        ...prev,
        components: prev.components.filter((c) => c.id !== compId),
      }));
    } catch {
      setFetchError("Failed to delete component. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  // ── Compatibility check ───────────────────────────────────
  const handleCompatibilityCheck = async () => {
    setCheckLoading(true);
    setCheckResult(null);
    try {
      const res = await api.post(`/compatibility/check/${id}`);
      setCheckResult(res.data);
      fetchProject();
    } catch (err) {
      setCheckResult({
        overall_status: "error",
        summary: err?.response?.data?.detail || "Check failed.",
        cross_component_issues: [],
        components: [],
      });
    } finally {
      setCheckLoading(false);
    }
  };

  // ── Generate ESS ──────────────────────────────────────────
  const handleGenerateESS = async (e, compId) => {
    e.stopPropagation();
    setEssLoading(compId);
    setEssResult(null);
    setEssError("");
    try {
      const res = await api.post(`/ess-gen/generate/${compId}`);
      setEssResult(res.data);
      setEssModalOpen(true);
      // Refresh so ESS badge updates on card
      fetchProject();
    } catch (err) {
      setEssError(
        err?.response?.data?.detail ||
          "ESS generation failed. Please try again.",
      );
    } finally {
      setEssLoading(null);
    }
  };

  // ── Download JSON ─────────────────────────────────────────
  const handleDownloadJSON = () => {
    if (!essResult) return;
    const blob = new Blob([JSON.stringify(essResult, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${essResult?.document_title || "ESS"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Download Word ─────────────────────────────────────────
  const handleDownloadWord = async () => {
    if (!essResult) return;
    setWordLoading(true);
    try {
      const res = await api.post(
        `/ess-gen/download-word`,
        { ess: essResult },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${essResult?.document_title || "ESS"}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setEssError("Word download failed. Please try again.");
    } finally {
      setWordLoading(false);
    }
  };

  // ── Edit project ──────────────────────────────────────────
  const handleOpenEditProject = () => {
    setEditProjectData({
      project_number: project.project_number || "",
      customer_name: project.customer_name || "",
      vessel_name: project.vessel_name || "",
      vessel_type: project.vessel_type || "",
      classification_society: project.classification_society || "",
      flag_state: project.flag_state || "",
      voltage_system: project.voltage_system || "",
      frequency: project.frequency || "",
      notes: project.notes || "",
    });
    setEditProjectError("");
    setEditProjectOpen(true);
  };

  const handleSaveEditProject = async () => {
    setEditProjectSaving(true);
    setEditProjectError("");
    try {
      await api.patch(`/projects/${id}`, editProjectData);
      setProject((prev) => ({ ...prev, ...editProjectData }));
      setEditProjectOpen(false);
    } catch {
      setEditProjectError("Failed to save project. Please try again.");
    } finally {
      setEditProjectSaving(false);
    }
  };

  // ── Edit component ────────────────────────────────────────
  const handleOpenEdit = (e, comp) => {
    e.stopPropagation();
    setEditComp(comp);
    setEditSpecs({ ...(comp.specifications || {}) });
    setEditTag(comp.tag_number || "");
    setEditError("");
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setEditError("");
    try {
      await api.patch(`/components/${editComp.id}`, {
        tag_number: editTag,
        specifications: editSpecs,
        compatibility_status: "pending",
      });
      setProject((prev) => ({
        ...prev,
        components: prev.components.map((c) =>
          c.id === editComp.id
            ? {
                ...c,
                tag_number: editTag,
                specifications: editSpecs,
                compatibility_status: "pending",
              }
            : c,
        ),
      }));
      setEditComp(null);
    } catch {
      setEditError("Failed to save changes. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────
  const getCompResult = (compId) =>
    checkResult?.components?.find((c) => c.component_id === compId);

  // ── Loading state ─────────────────────────────────────────
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: "4rem" }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );

  if (fetchError)
    return (
      <Alert severity="error" sx={{ borderRadius: "8px" }}>
        {fetchError}
      </Alert>
    );

  if (!project)
    return (
      <Alert severity="error" sx={{ borderRadius: "8px" }}>
        Project not found.
      </Alert>
    );

  const components = project.components || [];

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <Box>
      {/* ── Breadcrumb ─────────────────────────────────────── */}
      <Typography
        variant="body2"
        color="text.secondary"
        onClick={() => navigate("/")}
        sx={{ mb: "0.5rem", cursor: "pointer", "&:hover": { color: PRIMARY } }}
      >
        ← Back to Projects
      </Typography>

      {/* ── Global error banner ────────────────────────────── */}
      {fetchError && (
        <Alert
          severity="error"
          onClose={() => setFetchError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {fetchError}
        </Alert>
      )}

      {/* ── ESS error banner ───────────────────────────────── */}
      {essError && (
        <Alert
          severity="error"
          onClose={() => setEssError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {essError}
        </Alert>
      )}

      {/* ── Project Header Card ────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `2px solid ${PRIMARY}`,
          mb: "2rem",
          backgroundColor: "white",
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Left — project info */}
            <Grid item xs={12} sm={8}>
              <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
                {project.project_number}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: "0.25rem" }}
              >
                🚢 {project.vessel_name} &nbsp;|&nbsp; 🏢{" "}
                {project.customer_name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                {project.classification_society && (
                  <Chip
                    label={`Class: ${project.classification_society}`}
                    size="small"
                    sx={{
                      backgroundColor: "#e6f4f4",
                      color: PRIMARY,
                      fontWeight: "bold",
                    }}
                  />
                )}
                {project.voltage_system && (
                  <Chip
                    label={`⚡ ${project.voltage_system}`}
                    size="small"
                    sx={{
                      backgroundColor: "#fff3cd",
                      color: "#856404",
                      fontWeight: "bold",
                    }}
                  />
                )}
                {project.frequency && (
                  <Chip
                    label={`〰 ${project.frequency}`}
                    size="small"
                    sx={{
                      backgroundColor: "#e8f5e9",
                      color: "#00843d",
                      fontWeight: "bold",
                    }}
                  />
                )}
                {project.vessel_type && (
                  <Chip
                    label={project.vessel_type}
                    size="small"
                    sx={{
                      backgroundColor: "#f0eafa",
                      color: "#5c35a0",
                      fontWeight: "bold",
                    }}
                  />
                )}
                {project.flag_state && (
                  <Chip
                    label={`🏴 ${project.flag_state}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              {project.notes && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: "0.75rem", fontStyle: "italic" }}
                >
                  📝 {project.notes}
                </Typography>
              )}
            </Grid>

            {/* Right — action buttons */}
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: { sm: "flex-end" },
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/project/${id}/add-component`)}
                sx={{
                  backgroundColor: PRIMARY,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#004749" },
                }}
              >
                Add Component
              </Button>

              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleOpenEditProject}
                sx={{
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#e6f4f4" },
                }}
              >
                Edit Project
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate(`/schematic/edit/${project.id}`)}
                sx={{
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#e6f4f4" },
                }}
              >
                ✏️ Edit Schematic
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate(`/project/${project.id}/3d`)}
                sx={{
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#e6f4f4" },
                }}
              >
                🧊 View 3D Layout
              </Button>

              <Tooltip title="Reload project data">
                <IconButton
                  onClick={fetchProject}
                  sx={{ color: PRIMARY, alignSelf: { sm: "flex-end" } }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {components.length >= 1 && (
                <Button
                  variant="contained"
                  onClick={handleCompatibilityCheck}
                  disabled={checkLoading}
                  startIcon={
                    checkLoading ? (
                      <CircularProgress size={18} sx={{ color: "white" }} />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  sx={{
                    backgroundColor: "#5c35a0",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#4a2a85" },
                  }}
                >
                  {checkLoading
                    ? "Checking..."
                    : components.length === 1
                      ? "🔍 Validate Component"
                      : "🔍 Check Compatibility"}
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Compatibility Result Banner ─────────────────────── */}
      {checkResult &&
        (() => {
          const crossIssues = (checkResult.cross_component_issues || []).filter(
            (i) => (i.components_involved || []).length >= 2,
          );
          const selfIssues = (checkResult.cross_component_issues || []).filter(
            (i) => (i.components_involved || []).length < 2,
          );

          return (
            <Box sx={{ mb: "1.5rem" }}>
              {/* Cross-component banner */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  border: `2px solid ${
                    crossIssues.length
                      ? STATUS_BORDER[checkResult.overall_status]
                      : "#00843d"
                  }`,
                  backgroundColor: crossIssues.length
                    ? STATUS_BG[checkResult.overall_status]
                    : "#f0faf4",
                  mb: selfIssues.length ? "1rem" : 0,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      mb: "0.5rem",
                      color: crossIssues.length
                        ? STATUS_TEXT[checkResult.overall_status]
                        : "#00843d",
                    }}
                  >
                    {crossIssues.length
                      ? `❌ ${crossIssues.length} Cross-Component Issue${crossIssues.length > 1 ? "s" : ""} Found`
                      : "✅ No Issues Between Components"}
                  </Typography>

                  {!crossIssues.length && (
                    <Typography variant="body2" color="text.secondary">
                      All components are electrically compatible — voltage,
                      frequency, fault levels and earthing schemes are aligned.
                    </Typography>
                  )}

                  {crossIssues.length > 0 && (
                    <Box sx={{ mt: "0.75rem" }}>
                      {crossIssues.map((issue, i) => (
                        <Box
                          key={i}
                          sx={{
                            padding: "0.5rem 0.75rem",
                            borderRadius: "8px",
                            mb: "0.5rem",
                            backgroundColor:
                              issue.severity === "critical"
                                ? "#fce8ed"
                                : issue.severity === "warning"
                                  ? "#fff3cd"
                                  : "#e8f4fd",
                          }}
                        >
                          <Typography variant="body2">
                            <strong>
                              {issue.components_involved?.join(" ↔ ")}:
                            </strong>{" "}
                            {cleanIssueText(issue.issue)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Self-validation banner */}
              {selfIssues.length > 0 && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: "12px",
                    border: "2px solid #c47a00",
                    backgroundColor: "#fdf6e3",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mb: "0.5rem",
                        color: "#c47a00",
                      }}
                    >
                      ⚠️ {selfIssues.length} Component Self-Validation Issue
                      {selfIssues.length > 1 ? "s" : ""} Found
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: "0.75rem" }}
                    >
                      These issues are within individual components — not
                      cross-component conflicts.
                    </Typography>
                    {selfIssues.map((issue, i) => (
                      <Box
                        key={i}
                        sx={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "8px",
                          mb: "0.5rem",
                          backgroundColor:
                            issue.severity === "critical"
                              ? "#fce8ed"
                              : "#fff3cd",
                        }}
                      >
                        <Typography variant="body2">
                          <strong>{issue.components_involved?.[0]}:</strong>{" "}
                          {cleanIssueText(issue.issue)}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Box>
          );
        })()}

      {/* ── Components Header ──────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "1rem",
        }}
      >
        <Typography variant="h5" sx={{ color: DARK, fontWeight: "bold" }}>
          Components ({components.length})
        </Typography>
      </Box>

      {/* ── Empty State ────────────────────────────────────── */}
      {components.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            padding: "3rem",
            textAlign: "center",
            borderRadius: "12px",
            border: `2px dashed ${PRIMARY}`,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No components added yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: "1.5rem" }}
          >
            Start building the power system by adding components
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/project/${id}/add-component`)}
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            Add First Component
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {components.map((comp) => {
            const color = COMPONENT_COLORS[comp.component_type] || {
              bg: PRIMARY,
              light: "#e6f4f4",
            };
            const compResult = getCompResult(comp.id);
            const hasIssues =
              comp.compatibility_status === "fail" ||
              comp.compatibility_status === "warning";

            return (
              <Grid item xs={12} sm={6} md={4} key={comp.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "12px",
                    border: hasIssues
                      ? `2px solid ${comp.compatibility_status === "fail" ? "#b00033" : "#c47a00"}`
                      : "1px solid #e0e0e0",
                    borderTop: `4px solid ${color.bg}`,
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Top row */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "0.75rem",
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: color.bg,
                          width: 44,
                          height: 44,
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                        }}
                      >
                        {comp.component_type}
                      </Avatar>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {COMPAT_ICON[comp.compatibility_status || "pending"]}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {comp.compatibility_status || "pending"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Tag */}
                    <Typography
                      variant="h6"
                      sx={{ color: DARK, fontWeight: "bold" }}
                    >
                      {comp.tag_number || comp.component_type}
                    </Typography>

                    {/* Specs preview */}
                    <Box
                      sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        mt: "0.5rem",
                        mb: "0.5rem",
                      }}
                    >
                      {comp.specifications &&
                        Object.entries(comp.specifications)
                          .filter(([k, v]) => v && !isDimField(k))
                          .slice(0, 4)
                          .map(([k, v]) => (
                            <Typography
                              key={k}
                              variant="body2"
                              sx={{ color: "#555" }}
                            >
                              <strong>{k.replace(/_/g, " ")}:</strong> {v}
                            </Typography>
                          ))}
                      {comp.specifications &&
                        Object.entries(comp.specifications).filter(
                          ([k, v]) => isDimField(k) && v,
                        ).length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b45309",
                              fontWeight: "bold",
                              display: "block",
                              mt: "0.25rem",
                            }}
                          >
                            📐 Dimension limits set
                          </Typography>
                        )}
                    </Box>

                    {/* ESS badge */}
                    {comp.generated_ess && (
                      <Chip
                        label="✅ ESS Generated"
                        size="small"
                        sx={{
                          backgroundColor: "#e6f5ec",
                          color: "#00843d",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          mb: "0.5rem",
                        }}
                      />
                    )}

                    {/* Issues */}
                    {compResult?.issues?.length > 0 &&
                      (() => {
                        const criticalIssues = compResult.issues.filter(
                          (i) =>
                            i.toLowerCase().includes("critical") ||
                            i.toLowerCase().includes("exceed") ||
                            i.toLowerCase().includes("mismatch") ||
                            i.toLowerCase().includes("conflict"),
                        );
                        const warningIssues = compResult.issues.filter(
                          (i) => !criticalIssues.includes(i),
                        );
                        return (
                          <Box sx={{ mt: "0.5rem" }}>
                            {criticalIssues.length > 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#fff5f7",
                                  borderRadius: "6px",
                                  padding: "0.5rem",
                                  mb: "0.35rem",
                                  borderLeft: "3px solid #b00033",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#b00033",
                                    fontWeight: "bold",
                                    display: "block",
                                    mb: "0.25rem",
                                  }}
                                >
                                  ❌ Critical Issues:
                                </Typography>
                                {criticalIssues.map((issue, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{ color: "#b00033", display: "block" }}
                                  >
                                    • {cleanIssueText(issue)}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                            {warningIssues.length > 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#fffbf0",
                                  borderRadius: "6px",
                                  padding: "0.5rem",
                                  borderLeft: "3px solid #c47a00",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#c47a00",
                                    fontWeight: "bold",
                                    display: "block",
                                    mb: "0.25rem",
                                  }}
                                >
                                  ⚠ Spec Warnings:
                                </Typography>
                                {warningIssues.map((issue, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{ color: "#856404", display: "block" }}
                                  >
                                    • {cleanIssueText(issue)}
                                  </Typography>
                                ))}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#888",
                                    display: "block",
                                    mt: "0.25rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Click Edit to fill in the highlighted fields
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                    {/* Standards */}
                    {compResult?.standards_checked?.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: "0.5rem",
                        }}
                      >
                        {compResult.standards_checked.map((std, i) => (
                          <Chip
                            key={i}
                            label={std}
                            size="small"
                            sx={{
                              fontSize: "0.65rem",
                              backgroundColor: "#e6f4f4",
                              color: PRIMARY,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>

                  <Divider />

                  <CardActions
                    sx={{
                      justifyContent: "space-between",
                      padding: "0.6rem 1rem",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={(e) => handleOpenEdit(e, comp)}
                        sx={{
                          color: "#5c35a0",
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#f0eafa" },
                        }}
                      >
                        Edit
                      </Button>

                      {comp.generated_ess && (
                        <Button
                          size="small"
                          startIcon={<DescriptionIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEssResult(comp.generated_ess);
                            setEssModalOpen(true);
                          }}
                          sx={{
                            color: "#00843d",
                            fontWeight: "bold",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#e6f5ec" },
                          }}
                        >
                          View ESS
                        </Button>
                      )}

                      <Button
                        size="small"
                        startIcon={
                          essLoading === comp.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DescriptionIcon fontSize="small" />
                          )
                        }
                        disabled={essLoading === comp.id}
                        onClick={(e) => handleGenerateESS(e, comp.id)}
                        sx={{
                          color: comp.generated_ess ? "#888" : PRIMARY,
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": { backgroundColor: color.light },
                        }}
                      >
                        {essLoading === comp.id
                          ? "Generating..."
                          : comp.generated_ess
                            ? "Regenerate"
                            : "Generate ESS"}
                      </Button>
                    </Box>

                    <Tooltip title="Delete Component">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteComponent(e, comp.id)}
                          disabled={deleting === comp.id}
                          sx={{
                            color: "#b00033",
                            "&:hover": { backgroundColor: "#fce8ed" },
                          }}
                        >
                          {deleting === comp.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ══════════════════════════════════════════════════════
          EDIT PROJECT MODAL
      ══════════════════════════════════════════════════════ */}
      <Dialog
        open={editProjectOpen}
        onClose={() => setEditProjectOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{ backgroundColor: PRIMARY, color: "white", fontWeight: "bold" }}
        >
          ✏️ Edit Project Details
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#cce8e8" }}
          >
            {project?.project_number}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ padding: "1.5rem" }}>
          {editProjectError && (
            <Alert severity="error" sx={{ mb: "1rem", borderRadius: "8px" }}>
              {editProjectError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: "0.25rem" }}>
            {[
              { label: "Project Number", key: "project_number" },
              { label: "Customer Name", key: "customer_name" },
              { label: "Vessel Name", key: "vessel_name" },
              { label: "Flag State", key: "flag_state" },
              {
                label: "Voltage System",
                key: "voltage_system",
                placeholder: "e.g. 690V / 60Hz",
              },
              {
                label: "Frequency",
                key: "frequency",
                placeholder: "e.g. 60Hz",
              },
            ].map(({ label, key, placeholder }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={label}
                  placeholder={placeholder || ""}
                  value={editProjectData[key] || ""}
                  onChange={(e) =>
                    setEditProjectData({
                      ...editProjectData,
                      [key]: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": { borderColor: PRIMARY },
                    },
                  }}
                />
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Vessel Type"
                value={editProjectData.vessel_type || ""}
                onChange={(e) =>
                  setEditProjectData({
                    ...editProjectData,
                    vessel_type: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": { borderColor: PRIMARY },
                  },
                }}
              >
                {VESSEL_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Classification Society"
                value={editProjectData.classification_society || ""}
                onChange={(e) =>
                  setEditProjectData({
                    ...editProjectData,
                    classification_society: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": { borderColor: PRIMARY },
                  },
                }}
              >
                {CLASSIFICATION_SOCIETIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={editProjectData.notes || ""}
                onChange={(e) =>
                  setEditProjectData({
                    ...editProjectData,
                    notes: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": { borderColor: PRIMARY },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ padding: "1rem", gap: 1 }}>
          <Button
            onClick={() => setEditProjectOpen(false)}
            sx={{ color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditProject}
            disabled={editProjectSaving}
            startIcon={
              editProjectSaving ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#004749" },
            }}
          >
            {editProjectSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════
          EDIT COMPONENT MODAL
      ══════════════════════════════════════════════════════ */}
      {editComp && (
        <Dialog
          open={Boolean(editComp)}
          onClose={() => setEditComp(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle
            sx={{
              backgroundColor: PRIMARY,
              color: "white",
              fontWeight: "bold",
            }}
          >
            ✏️ Edit — {editComp.tag_number || editComp.component_type}
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#cce8e8" }}
            >
              {editComp.component_type}
              {editComp.compatibility_status === "fail" &&
                " — ⚠ Fix highlighted fields below"}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ padding: "1.5rem" }}>
            {editError && (
              <Alert severity="error" sx={{ mb: "1rem", borderRadius: "8px" }}>
                {editError}
              </Alert>
            )}

            {/* Issues reminder */}
            {(() => {
              const issues = getCompResult(editComp.id)?.issues || [];
              return issues.length > 0 ? (
                <Alert
                  severity="error"
                  sx={{ mb: "1.5rem", borderRadius: "8px" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: "0.25rem" }}
                  >
                    The following issues were detected — fix the highlighted
                    fields:
                  </Typography>
                  {issues.map((issue, i) => (
                    <Typography key={i} variant="body2">
                      • {cleanIssueText(issue)}
                    </Typography>
                  ))}
                </Alert>
              ) : null;
            })()}

            {/* Tag number */}
            <TextField
              fullWidth
              label="Tag Number"
              value={editTag}
              onChange={(e) => setEditTag(e.target.value)}
              sx={{
                mb: "1.5rem",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&.Mui-focused fieldset": { borderColor: PRIMARY },
                },
              }}
            />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: PRIMARY, mb: "1rem" }}
            >
              ⚡ {editComp.component_type} Specifications
            </Typography>

            {(() => {
              const issues = getCompResult(editComp.id)?.issues || [];
              const problematic = extractProblematicFields(issues);
              const specFields = COMPONENT_SPECS[editComp.component_type] || [];

              return (
                <Grid container spacing={2}>
                  {/* Dimension notice */}
                  {specFields.includes("max_height_mm") &&
                    [
                      "max_height_mm",
                      "max_width_mm",
                      "max_depth_mm",
                      "max_weight_kg",
                      "floor_loading_kn_m2",
                    ].some((k) => editSpecs[k]) && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            backgroundColor: "#fff8e1",
                            border: "1px solid #ffe082",
                            borderRadius: "8px",
                            padding: "0.6rem 1rem",
                            mt: "0.5rem",
                            mb: "0.25rem",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold", color: "#b45309" }}
                          >
                            📐 Physical Envelope — Do Not Exceed
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#78716c" }}
                          >
                            Leave blank if no dimensional restrictions apply.
                            Vendor will confirm actual dimensions at tender
                            stage.
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                  {specFields.map((field) => {
                    const isBad = problematic.has(field);
                    const isDim = isDimField(field);
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={field === "special_requirements" ? 12 : 6}
                        key={field}
                      >
                        <TextField
                          fullWidth
                          label={
                            isBad
                              ? `⚠ ${isDim ? formatDimLabel(field) : formatLabel(field)}`
                              : isDim
                                ? formatDimLabel(field)
                                : formatLabel(field)
                          }
                          value={editSpecs[field] || ""}
                          onChange={(e) =>
                            setEditSpecs({
                              ...editSpecs,
                              [field]: e.target.value,
                            })
                          }
                          multiline={field === "special_requirements"}
                          rows={field === "special_requirements" ? 3 : 1}
                          placeholder={
                            isDim ? "Leave blank if no restriction" : ""
                          }
                          sx={{
                            ...fieldStyle(isBad),
                            ...(isDim &&
                              !isBad && {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "#fffdf5",
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#b45309",
                                  },
                                },
                                "& label.Mui-focused": { color: "#b45309" },
                              }),
                          }}
                          helperText={
                            isBad ? "This field may be causing issues" : ""
                          }
                          FormHelperTextProps={{ sx: { color: "#b00033" } }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              );
            })()}
          </DialogContent>

          <DialogActions sx={{ padding: "1rem", gap: 1 }}>
            <Button onClick={() => setEditComp(null)} sx={{ color: "#666" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={editSaving}
              startIcon={
                editSaving ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                backgroundColor: PRIMARY,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#004749" },
              }}
            >
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ══════════════════════════════════════════════════════
          ESS MODAL
      ══════════════════════════════════════════════════════ */}
      <Dialog
        open={essModalOpen}
        onClose={() => setEssModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: PRIMARY,
            color: "white",
            fontWeight: "bold",
            fontSize: "1.1rem",
          }}
        >
          📄 {essResult?.document_title || "Engineering Specification Sheet"}
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#cce8e8" }}
          >
            {essResult?.revision}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ padding: "1.5rem" }}>
          {essResult && (
            <Box>
              {essResult.applicable_standards?.length > 0 && (
                <Box sx={{ mb: "1.5rem" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: PRIMARY, mb: "0.5rem" }}
                  >
                    Applicable Standards
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {essResult.applicable_standards.map((std, i) => (
                      <Chip
                        key={i}
                        label={std}
                        size="small"
                        sx={{
                          backgroundColor: "#e6f4f4",
                          color: PRIMARY,
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {essResult.sections &&
                Object.entries(essResult.sections).map(([key, value]) => (
                  <Box key={key} sx={{ mb: "1.5rem" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        color: PRIMARY,
                        mb: "0.5rem",
                        textTransform: "capitalize",
                        borderBottom: `2px solid ${PRIMARY}`,
                        pb: "0.25rem",
                      }}
                    >
                      {key.replace(/_/g, " ")}
                    </Typography>

                    {typeof value === "string" && (
                      <Typography variant="body2" color="text.secondary">
                        {value}
                      </Typography>
                    )}

                    {typeof value === "object" && !Array.isArray(value) && (
                      <Table size="small">
                        <TableBody>
                          {Object.entries(value).map(([k, v]) => (
                            <TableRow
                              key={k}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: "#f8f9fa",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: "40%",
                                  textTransform: "capitalize",
                                  fontSize: "0.8rem",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                {k.replace(/_/g, " ")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: "0.8rem",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                {Array.isArray(v) ? v.join(", ") : String(v)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {Array.isArray(value) && (
                      <Box
                        component="ul"
                        sx={{ margin: 0, paddingLeft: "1.5rem" }}
                      >
                        {value.map((item, i) => (
                          <Typography
                            key={i}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}

              {essResult.notes?.length > 0 && (
                <Box
                  sx={{
                    backgroundColor: "#fff3cd",
                    borderRadius: "8px",
                    padding: "0.75rem",
                    mt: "1rem",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: "0.5rem" }}
                  >
                    📝 Notes
                  </Typography>
                  {essResult.notes.map((note, i) => (
                    <Typography key={i} variant="body2">
                      • {note}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "1rem", gap: 1 }}>
          <Button onClick={() => setEssModalOpen(false)} sx={{ color: "#666" }}>
            Close
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadJSON}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            ⬇ Download JSON
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadWord}
            disabled={wordLoading}
            startIcon={
              wordLoading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : null
            }
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#004749" },
            }}
          >
            {wordLoading ? "Generating..." : "⬇ Download Word"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectDetail;
